/**
 * Âge des élèves, et filtrage par tranche.
 *
 * Mis en commun parce que deux écrans doivent s'accorder au cheveu près : la
 * liste de la classe et la carte de répartition par sexe affichent la même
 * tranche. Deux calculs séparés finiraient par diverger sur un cas limite —
 * un anniversaire du jour, une date illisible — et la carte annoncerait un
 * effectif que la liste ne montre pas.
 */

/**
 * Âge révolu, en années, d'après la date de naissance de la fiche.
 *
 * La date arrive au format `AAAA-MM-JJ`. On retranche une année tant que
 * l'anniversaire de cette année n'est pas passé : sans cela, un élève né en
 * décembre serait compté un an trop vieux pendant onze mois.
 *
 * @returns {number|null} `null` si la date manque, n'est pas lisible, ou se
 *                        situe dans le futur — dans ces cas l'âge est inconnu,
 *                        ce qui n'est pas la même chose que zéro.
 */
export function ageOf(dob) {
  if (!dob) return null;

  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthGap = today.getMonth() - birth.getMonth();
  if (monthGap < 0 || (monthGap === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

/**
 * Normalise une borne saisie.
 *
 * Un champ vidé rend `""` et non `0` : la chaîne vide veut dire « pas de
 * borne », le zéro veut dire « zéro an ». Les confondre ferait disparaître
 * les nourrissons dès qu'on efface le champ.
 */
export function parseBound(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
}

/** Une tranche est-elle réellement posée ? */
export function hasAgeRange(range) {
  if (!range) return false;
  return range.min !== null || range.max !== null;
}

/**
 * Élèves dont l'âge tombe dans la tranche.
 *
 * Sans tranche, la liste revient telle quelle — même tableau, même ordre :
 * le filtre ne réordonne rien.
 *
 * Un élève sans date de naissance exploitable n'a pas d'âge connu : il sort
 * de la liste dès qu'une borne est posée, plutôt que d'être gardé à tout
 * hasard dans un résultat censé répondre à un critère d'âge. Les écrans le
 * signalent, pour qu'on ne croie pas à une disparition.
 */
export function filterByAge(students, range) {
  const list = students || [];
  if (!hasAgeRange(range)) return list;

  const { min, max } = range;
  return list.filter((student) => {
    const age = ageOf((student || {}).dob);
    if (age === null) return false;
    if (min !== null && age < min) return false;
    if (max !== null && age > max) return false;
    return true;
  });
}

/** Combien d'élèves n'ont pas de date de naissance exploitable. */
export function countWithoutDob(students) {
  return (students || []).filter((student) => ageOf((student || {}).dob) === null)
    .length;
}
